import { createContext, useContext } from "react";

import { ChainDirection } from "../../types/valueChain";
import { ChainCardRef, ChainDetailRef } from "./valueChainState";

// What the cards of the canvas can do to the page. Given through a context,
// so that a card node carries only its card.
export interface ChainActions {
  // The cards opened beyond the read of the root
  opened: readonly ChainCardRef[];
  // The opened cards whose read has not arrived yet
  loading: readonly ChainCardRef[];
  // The entity whose detail is open; null for none
  detail: ChainDetailRef | null;
  // The direction the root is read in
  direction: ChainDirection;
  // Steps the root is read towards its parts: a card that far out is at the
  // edge of the read, and its parts are not known yet
  partsDepth: number;
  // Steps the root is read towards its hosts, when the direction reads them
  hostsDepth: number;
  // Opens a card's next column, or a stated class in place; closes it again
  onToggle: (card: ChainCardRef) => void;
  // Opens the detail of an entity
  onOpenDetail: (detail: ChainDetailRef) => void;
  // Shows every card a collapsed row stands for, by the row's id
  onShowAll: (row: string) => void;
  // Opens the parts side the stub stands for
  onOpenParts: () => void;
  // Reads the root in another direction
  onDirectionChange: (direction: ChainDirection) => void;
  // Makes a card the root: the chain is read afresh around it
  onCentre: (card: ChainCardRef) => void;
}

const noop = () => undefined;

export const ChainActionsContext = createContext<ChainActions>({
  opened: [],
  loading: [],
  detail: null,
  direction: "parts",
  partsDepth: 1,
  hostsDepth: 1,
  onToggle: noop,
  onOpenDetail: noop,
  onShowAll: noop,
  onOpenParts: noop,
  onDirectionChange: noop,
  onCentre: noop,
});

export const useChainActions = (): ChainActions =>
  useContext(ChainActionsContext);
